import { useState } from "react";
import { brl } from "@/lib/api";
import { Button, Input, Select } from "@/components/ui-kit";
import { AlertTriangle, Link2, PlusCircle, EyeOff, Filter, SlidersHorizontal, ChevronDown } from "lucide-react";

/**
 * Barra de resumo da importação.
 *
 * Uma NF-e de compra tem dezenas de linhas, e antes disto o botão "Confirmar
 * entrada" era um salto no escuro: não dava para saber quantas iam ser criadas,
 * quantas vinculadas nem quanto de mercadoria estava entrando. Aqui isso fica à
 * vista, e o que ainda precisa de decisão vira um filtro — não um erro depois do
 * clique.
 *
 * `sticky`: numa nota de 40 itens o topo sai da tela na primeira rolagem, e o
 * contador de pendências é justamente o que se quer acompanhar enquanto se
 * desce a lista.
 *
 * ── O que fica grudado, e o que não ────────────────────────────────────────
 * A barra tinha CINCO blocos empilhados — contadores, frete/despesas/desconto,
 * prova do rateio, aviso de pendência e ações em massa — e passava de 200px
 * presos no topo, cobrindo as linhas que a pessoa estava justamente tentando
 * revisar.
 *
 * Só que a maior parte disso se preenche UMA VEZ, no começo: frete, desconto e
 * margens não mudam enquanto se desce a lista de 74 itens. O que precisa
 * acompanhar a rolagem é o placar e o filtro de pendências.
 *
 * Então a barra grudada virou uma linha, e o resto entrou num painel que abre
 * quando se precisa dele. Abre sozinho quando há custo a lançar e a nota ainda
 * não foi tocada — é o primeiro passo de quem chega.
 */
export default function ImportSummary({
  resumo, totaisNota, soPendentes, onTogglePendentes,
  categorias, categoriaEmMassa, onCategoriaEmMassa,
  margemMin, onMargemMin, margemMax, onMargemMax, onAplicarMargem, temNovos,
  custos, onCusto, tipoDesconto, onTipoDesconto, descontoEfetivo, rateio,
}) {
  /* Aberto por padrão só na primeira olhada: quem acabou de subir a nota precisa
     lançar frete e margem antes de tudo. Depois de fechado uma vez, fica
     fechado — a revisão item a item quer a tela inteira. */
  const [aberto, setAberto] = useState(true);

  // O rateio distribuído tem de bater com o que foi digitado no cabeçalho. A
  // checagem é visual de propósito: o cálculo já garante o fechamento (o resíduo
  // vai para a maior linha), e o que a tela precisa é PROVAR isso.
  const extraEsperado = (Number(custos?.frete) || 0) + (Number(custos?.outros) || 0);
  const fechaExtra = !rateio || Math.abs(rateio.extra - extraEsperado) < 0.01;
  const fechaDesconto = !rateio || Math.abs(rateio.desconto - (Number(descontoEfetivo) || 0)) < 0.01;

  /* O que já foi lançado, resumido no botão. Sem isto, fechar o painel
     esconderia o frete e o desconto — e alguém confirmaria a entrada sem
     lembrar se lançou. */
  const lancado = [
    (Number(custos?.frete) || 0) > 0 && `frete ${brl(custos.frete)}`,
    (Number(custos?.outros) || 0) > 0 && `desp. ${brl(custos.outros)}`,
    (Number(descontoEfetivo) || 0) > 0 && `desc. −${brl(descontoEfetivo)}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="sticky top-0 z-10 -mx-1 px-1 pb-2 pt-1 bg-slate-100/80 backdrop-blur"
      data-testid="stock-import-summary">
      <div className="rounded-xl border border-slate-200 bg-white">
        {/* A LINHA GRUDADA. Só o que precisa acompanhar a rolagem: quantos vão
            para onde, quanto entra de mercadoria, e o que ainda espera decisão. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm px-3 py-2.5">
          <span className="flex items-center gap-1.5 text-slate-700">
            <Link2 size={15} className="text-brand-600" aria-hidden />
            <b className="font-mono">{resumo.vincular}</b> a vincular
          </span>
          <span className="flex items-center gap-1.5 text-slate-700">
            <PlusCircle size={15} className="text-emerald-600" aria-hidden />
            <b className="font-mono">{resumo.criar}</b> a criar
          </span>
          {resumo.ignorar > 0 && (
            <span className="flex items-center gap-1.5 text-slate-400">
              <EyeOff size={15} aria-hidden /><b className="font-mono">{resumo.ignorar}</b> ignorada(s)
            </span>
          )}
          <span className="text-slate-500">
            <b className="font-mono text-slate-800">{resumo.pecas}</b> peças ·{" "}
            <b className="font-mono text-slate-800">{brl(resumo.custo)}</b> de custo real
          </span>

          <div className="flex-1" />

          {/* O filtro só existe quando há o que filtrar: numa nota já revisada
              ele seria um controle morto ocupando a barra. */}
          {resumo.pendentes > 0 && (
            <Button variant={soPendentes ? "primary" : "secondary"} size="sm" icon={Filter}
              onClick={onTogglePendentes} data-testid="stock-import-filter-pending">
              {soPendentes ? "Mostrando pendentes" : `Ver ${resumo.pendentes} pendente(s)`}
            </Button>
          )}

          {/* Abre o que se preenche uma vez só. O resumo do que está lançado
              vem no rótulo, para não precisar abrir só para conferir. */}
          <Button variant="ghost" size="sm" icon={SlidersHorizontal} onClick={() => setAberto((v) => !v)}
            aria-expanded={aberto} data-testid="stock-import-toggle-setup" className="gap-1.5">
            Custos e margens
            {!aberto && lancado && <span className="font-mono text-xs text-slate-500">· {lancado}</span>}
            <ChevronDown size={14} className={`transition-transform ${aberto ? "rotate-180" : ""}`} aria-hidden />
          </Button>
        </div>

        {/* AVISO DE PENDÊNCIA fica na linha grudada: é o que diz se dá para
            confirmar, e some sozinho quando não há mais nada esperando. */}
        {resumo.pendentes > 0 && (
          <p className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200 px-3 py-1.5"
            data-testid="stock-import-pending-warning">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" aria-hidden />
            <span>
              <b>{resumo.pendentes}</b> linha(s) esperando você.
              {resumo.bloqueadas > 0
                ? ` ${resumo.bloqueadas} não pode(m) ser importada(s) sem um campo obrigatório.`
                : " Nenhuma bloqueia a importação — são palpites por nome, que valem uma conferida."}
            </span>
          </p>
        )}

        {aberto && (
        <div className="p-3 pt-0 space-y-3">

        {/* Formação de custos: o cabeçalho é EDITÁVEL e recalcula todas as
            linhas na hora. O frete é o que mais distorce margem em loja
            ortopédica — mercadoria leve e cara, frete caro — e nem sempre vem
            certo no XML: o fornecedor lança no total, cobra à parte, ou o valor
            combinado é outro. */}
        {custos && (
          <div className="border-t border-slate-100 pt-2.5 space-y-2" data-testid="stock-import-costs">
            <div className="flex flex-wrap items-end gap-3">
              <span className="text-xs text-slate-500 pb-2">Rateado entre os itens:</span>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor="custo-frete">Frete</label>
                <Input id="custo-frete" type="number" step="0.01" min="0" size="sm" mono
                  value={custos.frete} onChange={(e) => onCusto("frete", e.target.value)}
                  data-testid="stock-import-cost-frete" className="w-28 text-sm" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor="custo-outros">Despesas op.</label>
                <Input id="custo-outros" type="number" step="0.01" min="0" size="sm" mono
                  value={custos.outros} onChange={(e) => onCusto("outros", e.target.value)}
                  data-testid="stock-import-cost-outros" className="w-28 text-sm" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor="custo-desconto">Desconto</label>
                <div className="flex items-center gap-1.5">
                  {/* Segmentado, não select: as duas unidades ficam à vista. A
                      NF traz o desconto em R$ (vDesc); % é a negociação. */}
                  <div role="group" aria-label="Unidade do desconto"
                    className="flex shrink-0 rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                    {[["fixed", "R$"], ["percentage", "%"]].map(([id, rotulo]) => (
                      <button key={id} type="button" onClick={() => onTipoDesconto(id)}
                        aria-pressed={tipoDesconto === id} data-testid={`stock-import-discount-${id}`}
                        className={`h-8 w-9 text-xs font-semibold rounded-md transition-colors ${
                          tipoDesconto === id ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                        {rotulo}
                      </button>
                    ))}
                  </div>
                  <Input id="custo-desconto" type="number" step="0.01" min="0" size="sm" mono
                    value={custos.desconto} onChange={(e) => onCusto("desconto", e.target.value)}
                    data-testid="stock-import-cost-desconto" className="w-24 text-sm" />
                  {/* Valor efetivo ao lado: em %, o número digitado não diz
                      quanto sai de verdade, e é o efetivo que entra no rateio. */}
                  {tipoDesconto === "percentage" && (
                    <span className="text-xs text-slate-500 pb-2 whitespace-nowrap" data-testid="stock-import-discount-effective">
                      ≡ {brl(descontoEfetivo)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Prova de que o rateio fecha. É o que permite confiar no custo
                sem conferir item a item na calculadora. */}
            {rateio && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="stock-import-alloc-totals">
                <span className="text-slate-500">Produtos <b className="font-mono text-slate-800">{brl(rateio.base)}</b></span>
                <span className={fechaExtra ? "text-slate-500" : "text-red-600 font-semibold"}>
                  Rateio + <b className="font-mono">{brl(rateio.extra)}</b>
                  {fechaExtra ? " ✓" : ` ≠ ${brl(extraEsperado)}`}
                </span>
                <span className={fechaDesconto ? "text-emerald-600" : "text-red-600 font-semibold"}>
                  Desconto − <b className="font-mono">{brl(rateio.desconto)}</b>
                  {fechaDesconto ? " ✓" : ` ≠ ${brl(descontoEfetivo)}`}
                </span>
                <span className="text-slate-500">
                  Custo total <b className="font-mono text-slate-800">{brl(rateio.final)}</b>
                </span>
                {totaisNota?.nota > 0 && (
                  <span className="text-slate-400">· nota {brl(totaisNota.nota)}</span>
                )}
              </div>
            )}

            {(totaisNota?.icms_base > 0 || totaisNota?.icms_st_base > 0) && (
              <p className="text-[11px] text-slate-400" data-testid="stock-import-icms-bases">
                Base cálc. ICMS {brl(totaisNota.icms_base)} · Base cálc. ICMS Subst. {brl(totaisNota.icms_st_base)}
              </p>
            )}
          </div>
        )}


        {/* Ações em massa: uma nota de 40 itens novos exigia escolher categoria
            40 vezes e digitar 40 preços. */}
        {temNovos && (
          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-2.5">
            <span className="text-xs text-slate-500 pb-2">Para todos os itens novos:</span>
            <Select size="sm" value={categoriaEmMassa} onChange={(e) => onCategoriaEmMassa(e.target.value)}
              aria-label="Aplicar categoria a todos os itens novos" className="w-auto text-sm"
              data-testid="stock-import-bulk-category">
              <option value="">Aplicar categoria…</option>
              {categorias.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </Select>
            {/* Duas margens: a MÁXIMA gera o preço de etiqueta, a MÍNIMA vira
                o piso de venda do produto — o menor preço que o PDV aceita
                depois do desconto. */}
            <div className="flex items-end gap-1.5">
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor="margem-min">Margem mín.</label>
                <Input id="margem-min" type="number" min="0" max="900" size="sm" mono value={margemMin}
                  onChange={(e) => onMargemMin(e.target.value)} data-testid="stock-import-margin-min"
                  className="w-16 text-center text-sm" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor="margem-max">Margem máx.</label>
                <Input id="margem-max" type="number" min="0" max="900" size="sm" mono value={margemMax}
                  onChange={(e) => onMargemMax(e.target.value)} data-testid="stock-import-margin-max"
                  className="w-16 text-center text-sm" />
              </div>
              <span className="text-xs text-slate-500 pb-2">%</span>
              <Button variant="secondary" size="sm" onClick={onAplicarMargem}
                data-testid="stock-import-apply-margin">Aplicar sobre o custo</Button>
            </div>
          </div>
        )}
        </div>
        )}
      </div>
    </div>
  );
}
